import React, { useState } from 'react'
import { Button } from 'antd'
import AiDraftWorkflowOrchestrator from '../AiDraftPopover/WorkflowOrchestrator'
import AiDraftErrorBoundary from '../AiDraftPopover/ErrorBoundary'
import { getRecipientBucket } from '../AiDraftPopover/Editor'
import type { AiDraftEditorProps } from '../AiDraftPopover/types'
import type { DraftAction } from '../../../models/aiAnalysis/workflow'
import { handoffStore } from '../../../utils/aiAnalysis/handoffStore'
import { AI_SECTION_EVENTS, draftAgeSeconds, trackSectionEvent } from '../../../utils/aiAnalysis/rumFunnel'
import RecipientPill from '../RecipientPill'
import styles from './styles.module.scss'

interface SuggestedCardProps {
  action: DraftAction
  // The held card never renders, so this can only disable siblings.
  handoffInProgress: boolean
  onCardClose: () => void
  onDecisionStateStale?: () => void
}

interface CollapsedDraftProps {
  editorProps: AiDraftEditorProps
  disabled: boolean
  onTake: () => void
  onDeclined: () => void
}

// "No response needed" is the decline the orchestrator supplied through the
// editor props — the chrome holds no decide logic of its own.
const CollapsedDraft: React.FC<CollapsedDraftProps> = ({ editorProps, disabled, onTake, onDeclined }) => {
  const [isDeclining, setIsDeclining] = useState(false)
  const label = getRecipientBucket(editorProps.action)

  // No reset: the orchestrator's decline resolution always replaces this
  // chrome (card removed, next step, or error display).
  const handleNoResponseNeeded = async () => {
    setIsDeclining(true)
    if (await editorProps.onDecline()) onDeclined()
  }

  return (
    <div className={styles.collapsedCard}>
      <div className={styles.draftSummary}>
        <RecipientPill>{label}</RecipientPill>
        <p className={styles.draftPreview}>{editorProps.currentText}</p>
      </div>
      <div className={styles.cardActions}>
        <Button
          className={styles.actionButton}
          aria-label={`Use or edit comment to ${label}`}
          data-test="use-or-edit-button"
          onClick={onTake}
          disabled={disabled || isDeclining}
        >
          Use or edit
        </Button>
        <Button
          className={styles.actionButton}
          aria-label={`No response needed for comment to ${label}`}
          data-test="no-response-needed-button"
          onClick={handleNoResponseNeeded}
          loading={isDeclining}
          aria-busy={isDeclining}
          disabled={disabled}
        >
          No response needed
        </Button>
      </div>
    </div>
  )
}

const SuggestedCard: React.FC<SuggestedCardProps> = ({
  action,
  handoffInProgress,
  onCardClose,
  onDecisionStateStale
}) => {
  const handleTake = () => {
    trackSectionEvent(AI_SECTION_EVENTS.TAKE, { action_id: action.id })
    handoffStore.take(action)
  }

  const handleDeclined = () => {
    trackSectionEvent(AI_SECTION_EVENTS.DECIDE, { action_id: action.id, draft_age_s: draftAgeSeconds(action) })
  }

  const renderEditor = (editorProps: AiDraftEditorProps) => (
    <CollapsedDraft
      editorProps={editorProps}
      disabled={handoffInProgress}
      onTake={handleTake}
      onDeclined={handleDeclined}
    />
  )

  const handleFatalDismiss = () => {
    onCardClose()
    onDecisionStateStale?.()
  }

  return (
    <div className={styles.card} data-test="suggested-card" data-recipient={getRecipientBucket(action)}>
      <AiDraftErrorBoundary onClose={onCardClose}>
        <AiDraftWorkflowOrchestrator
          actions={[action]}
          isOpen={true}
          onClose={onCardClose}
          onFatalDismiss={handleFatalDismiss}
          autoFocus={false}
          showProvenanceHeader={false}
          renderEditor={renderEditor}
        />
      </AiDraftErrorBoundary>
    </div>
  )
}

export default SuggestedCard
